"use client";

import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from "react";
import renderModalByType from "@/providers/modal-provider/modal.utils";
import { ModalType } from "@/providers/modal-provider/modal-context.types";
import { Modal } from "@/components/modal/base/modal";
import useModal from "@/hooks/use-modal";

type ModalContextState = {
  isOpen: boolean;
  isClosing: boolean;
  showModal: (modalType: ModalType, showCloseIcon?: boolean) => void;
  hideModal: () => void;
};

const initialState: ModalContextState = {
  isOpen: false,
  isClosing: false,
  showModal: () => {},
  hideModal: () => {},
};

const ModalContext = createContext(initialState);
export const useGlobalModalContext = () => useContext(ModalContext);

export const ModalProvider = ({ children }: { children: React.ReactNode }) => {
  const [modal, setModal] = useState<React.JSX.Element | null>(null);
  const [showCloseIcon, setShowCloseIcon] = useState<boolean>(true);
  const { closeModal, openModal, isClosing, isOpen } = useModal();

  const showModal = useCallback(
    (modalType: ModalType, showCloseIcon: boolean = true) => {
      setModal(renderModalByType(modalType));
      setShowCloseIcon(showCloseIcon);
      openModal();
    },
    [openModal],
  );

  const hideModal = useCallback(() => {
    closeModal();
    setTimeout(() => setModal(null), 300);
  }, [closeModal]);

  const modalValue = useMemo(
    () => ({ isOpen, isClosing, showModal, hideModal: closeModal }),
    [showModal, closeModal],
  );

  return (
    <ModalContext.Provider value={modalValue}>
      <Modal
        isOpen={isOpen}
        isClosing={isClosing}
        closeModal={closeModal}
        showCloseIcon={showCloseIcon}
      >
        <div id={"open_modal"}>{modal}</div>
      </Modal>
      {children}
    </ModalContext.Provider>
  );
};
